/**
 * @cfleet/auth - CSRF Utilities
 *
 * Provides CSRF (Cross-Site Request Forgery) token handling for Better Auth.
 * Better Auth uses cookie-based CSRF protection for mutation endpoints.
 *
 * CSRF tokens are automatically set by Better Auth in cookies and must be
 * included in request headers for POST/PUT/DELETE operations.
 */

/**
 * Cookie name used by Better Auth for CSRF tokens
 */
const CSRF_COOKIE_NAME = 'better-auth.csrf_token';

/**
 * Header name for CSRF token
 */
const CSRF_HEADER_NAME = 'X-CSRF-Token';

/**
 * Extract CSRF token from document cookies
 *
 * Better Auth stores the CSRF token in a cookie named 'better-auth.csrf_token'.
 * This function parses the cookie and returns the token value.
 *
 * @returns The CSRF token if found, null otherwise
 *
 * @example
 * ```ts
 * const token = getCSRFToken();
 * if (token) {
 *   headers['X-CSRF-Token'] = token;
 * }
 * ```
 */
export function getCSRFToken(): string | null {
  // Check if we're in a browser environment
  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === CSRF_COOKIE_NAME && value) {
        return decodeURIComponent(value);
      }
    }
  } catch {
    // Cookie parsing failed, return null
    console.warn('[csrf] Failed to parse CSRF token from cookies');
  }

  return null;
}

/**
 * Add CSRF token header to an existing headers object
 *
 * If a valid CSRF token is found in cookies, it will be added
 * to the headers under 'X-CSRF-Token'. If no token is found,
 * the original headers are returned unchanged.
 *
 * @param headers - Existing headers (can be HeadersInit, Headers, or plain object)
 * @returns Headers with CSRF token added if available
 *
 * @example
 * ```ts
 * const headers = addCSRFHeader({
 *   'Content-Type': 'application/json',
 * });
 * // Result: { 'Content-Type': 'application/json', 'X-CSRF-Token': '...' }
 * ```
 */
export function addCSRFHeader(headers: HeadersInit = {}): HeadersInit {
  const token = getCSRFToken();

  if (!token) {
    return headers;
  }

  // Handle Headers instance
  if (headers instanceof Headers) {
    const newHeaders = new Headers(headers);
    newHeaders.set(CSRF_HEADER_NAME, token);
    return newHeaders;
  }

  // Handle array of [key, value] pairs
  if (Array.isArray(headers)) {
    return [...headers, [CSRF_HEADER_NAME, token]];
  }

  // Handle plain object
  return {
    ...headers,
    [CSRF_HEADER_NAME]: token,
  };
}

/**
 * Get the current origin for CORS requests
 *
 * Returns window.location.origin in browser environments,
 * or a default origin for test/SSR environments.
 */
function getOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  // Default for test environments (jsdom) or SSR
  return 'http://localhost:3000';
}

/**
 * Create headers for a Better Auth request with CSRF protection
 *
 * Convenience function that creates a standard set of headers for
 * Better Auth API requests, including Content-Type, Origin, and CSRF token.
 *
 * @param additionalHeaders - Optional additional headers to include
 * @returns Headers object ready for fetch
 *
 * @example
 * ```ts
 * const response = await fetch('/auth/sign-out', {
 *   method: 'POST',
 *   headers: createBetterAuthHeaders(),
 *   credentials: 'include',
 * });
 * ```
 */
export function createBetterAuthHeaders(
  additionalHeaders: Record<string, string> = {}
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Origin header is required by Better Auth for CORS protection
    Origin: getOrigin(),
    ...additionalHeaders,
  };

  const csrfToken = getCSRFToken();
  if (csrfToken) {
    headers[CSRF_HEADER_NAME] = csrfToken;
  }

  return headers;
}

/**
 * Check if CSRF token is present in cookies
 *
 * Useful for debugging or conditional logic based on auth state.
 *
 * @returns true if a CSRF token cookie exists
 */
export function hasCSRFToken(): boolean {
  return getCSRFToken() !== null;
}

/**
 * Clear CSRF token by expiring the cookie
 *
 * This is typically not needed as Better Auth manages the cookie lifecycle,
 * but can be useful for testing or manual cleanup.
 *
 * Note: This only works if the cookie was set without HttpOnly flag.
 */
export function clearCSRFToken(): void {
  if (typeof document === 'undefined') {
    return;
  }

  // Set cookie with expired date to remove it
  document.cookie = `${CSRF_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}
